import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { RestService } from '../../../services/rest/rest.service';
import { AccountsIncome, EquitiesChange, PeriodType, PortfolioPerformance } from './balances-summary.domain';
import { TrendDetails } from './trend-details.domain';

@Injectable()
export class BalancesSummaryPerformanceRestService {
  private readonly restService = inject(RestService);

  private static readonly CACHE_CAPACITY: number = 1;
  private cacheMap: Map<string, Observable<PortfolioPerformance>>;

  constructor() {
    this.cacheMap = new Map<string, Observable<PortfolioPerformance>>();
  }

  getPortfolioPerformance(periodType: PeriodType): Observable<PortfolioPerformance> {
    periodType = this.defaultPeriodTypeIfUndefined(periodType);
    // we make this call a few seconds apart in two different controllers, by caching it we save the redundant call
    if (!this.cacheMap.has(periodType)) {
      this.cacheMap.set(
        periodType,
        this.requestPortfolioPerformance(periodType)
          .pipe(
            catchError((error: unknown): Observable<never> => {
              this.cacheMap.delete(periodType);
              return throwError(() => error);
            }),
            shareReplay(BalancesSummaryPerformanceRestService.CACHE_CAPACITY)
          )
      );
    }

    return this.cacheMap.get(periodType);
  }

  getEquitiesChange(periodType: PeriodType): Observable<EquitiesChange> {
    return this.restService.getResource('portfolio.equityChange').get({
      params: this.getPeriodTypeParams(periodType)
    });
  }

  getBalancePerformanceSummary(periodType?: PeriodType): Observable<TrendDetails> {
    return this.restService.getResource('portfolio.balancePerformanceSummary').get({
      params: this.getPeriodTypeParams(periodType)
    });
  }

  getAccountsIncome(): Observable<AccountsIncome> {
    return this.restService.getResource('portfolio.accountincome').get();
  }

  private getPeriodTypeParams(periodType: PeriodType): { periodType: PeriodType } {
    return { periodType: this.defaultPeriodTypeIfUndefined(periodType) };
  }

  private defaultPeriodTypeIfUndefined(periodType: PeriodType): PeriodType {
    return periodType === undefined ? PeriodType.LAST_MONTH : periodType;
  }

  private requestPortfolioPerformance(periodType: PeriodType): Observable<PortfolioPerformance> {
    return this.restService.getResource('portfolio.performance').get({
      params: this.getPeriodTypeParams(periodType)
    });
  }
}

